/* eslint-disable no-console */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { makeApiRequest } from "../utils";
import { VALID_VENDOR_SEARCH_JOB_NAMES } from "../../constants/queueConstants";

export const useBrandQuery = (brandId) => {
  return useQuery({
    queryKey: ["brand", brandId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}`,
        method: "GET"
      });
      return response;
    },
    keepPreviousData: true
  });
};

export const useCrawledPagesQuery = (brandId) => {
  return useQuery({
    queryKey: ["crawledPages", brandId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/crawled-pages`,
        method: "GET"
      });
      return response;
    }
  });
};

export const useUpdateVendorMeta = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates) => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/vendor-meta`,
        method: "PUT",
        body: updates,
        json: true
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand", brandId] });
    }
  });
};

export const useStartBrandCrawl = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/start-crawl`,
        method: "POST",
        body: {},
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crawledPages", brandId] });
    }
  });
};

export const useStartBrandAnalysis = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ discoveryPrompt, analysisPrompt }) => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/start-analysis`,
        method: "POST",
        body: { discoveryPrompt, analysisPrompt },
        json: true
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crawledPages", brandId] });
    }
  });
};

export const useStartVendorRegexSearch = () => {
  return useMutation({
    mutationFn: async ({ brandName, pattern }) => {
      const response = await makeApiRequest({
        endpoint: "/api/vendors/start-vendor-job",
        method: "POST",
        body: {
          jobData: {
            jobName: VALID_VENDOR_SEARCH_JOB_NAMES.START_VENDOR_REGEX_SEARCH,
            brandName,
            pattern
          }
        },
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    }
  });
};

export const useGetMatchedVendors = (brandNameOrVendorId, brandMatch = true) => {
  const queryString = brandMatch ? `brandName=${brandNameOrVendorId}` : `vendorId=${brandNameOrVendorId}`;
  return useQuery({
    queryKey: ["matchedVendors", brandNameOrVendorId, brandMatch],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: "/api/vendors/matched-vendors?" + queryString,
        method: "GET"
      });
      return response;
    },
    enabled: !!brandNameOrVendorId,
    staleTime: 30000,
    cacheTime: 5 * 60 * 1000
  });
};

export const useCreateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (brandData) => {
      const response = await makeApiRequest({
        endpoint: "/api/brands",
        method: "POST",
        body: brandData,
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
    }
  });
};

export const useScrapedPageQuery = (brandId, pageId) => {
  return useQuery({
    queryKey: ["scrapedPage", brandId, pageId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/scraped-pages/${pageId}`,
        method: "GET"
      });
      return response;
    },
    enabled: !!pageId
  });
};

export const useStartSinglePageAnalysis = (brandId, pageId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (prompts) => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/scraped-pages/${pageId}/analyze`,
        method: "POST",
        body: prompts,
        json: true
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scrapedPage", brandId, pageId] });
    }
  });
};

export const useGenerateBrandEmbeddings = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/generate-brand-embeddings`,
        method: "POST",
        body: {},
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandEmbeddings", brandId] });
    }
  });
};

export const useGenerateBrandEmbeddingsFromUrl = (brandId, url) => {
  return useMutation(async () => {
    const res = await makeApiRequest({
      endpoint: `/api/brands/${brandId}/generate-brand-embeddings-from-url`,
      method: "POST",
      body: { url },
      json: true
    });
    if (res.error) {
      throw new Error(res.error);
    }
    return res;
  });
};

export const useStartEmbeddingCrawl = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/start-embedding-crawl`,
        method: "POST",
        body: {},
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandEmbeddings", brandId] });
    }
  });
};

export const useBrandEmbeddingsQuery = (brandId) => {
  return useQuery({
    queryKey: ["brandEmbeddings", brandId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/brand-embeddings`,
        method: "GET"
      });
      return response;
    },
    enabled: !!brandId
  });
};

export const useBrandRatingsQuery = (brandId) => {
  return useQuery({
    queryKey: ["brandRatings", brandId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/ratings`,
        method: "GET"
      });
      return response;
    },
    enabled: !!brandId
  });
};

export const useBrandRatingRubricsQuery = () => {
  return useQuery({
    queryKey: ["brandRatingRubrics"],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: "/api/brands/rating-rubrics",
        method: "GET"
      });
      return response;
    }
  });
};

export const useCreateBrandRating = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (brandRatingRubricId) => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/ratings`,
        method: "POST",
        body: { brandRatingRubricId },
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandRatings", brandId] });
    }
  });
};

export const useBrandRatingQuery = (brandId, ratingId) => {
  return useQuery({
    queryKey: ["brandRating", brandId, ratingId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/ratings/${ratingId}`,
        method: "GET"
      });
      return response;
    },
    enabled: !!brandId && !!ratingId
  });
};

export const useDeleteBrandRating = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ratingId) => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/ratings/${ratingId}`,
        method: "DELETE"
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandRatings", brandId] });
    }
  });
};

export const useQueryBrand = (brandId) => {
  return useMutation({
    mutationFn: async (prompt) => {
      const response = await makeApiRequest({
        endpoint: `/api/brands/${brandId}/query`,
        method: "POST",
        body: { prompt },
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    }
  });
};

export const useBrandIngredientsQuery = (brandId) => {
  return useQuery({
    queryKey: ["brandIngredients", brandId],
    queryFn: async () => {
      const response = await makeApiRequest({
        endpoint: `/api/brand_ingredients/${brandId}`,
        method: "GET"
      });
      return response;
    },
    enabled: !!brandId
  });
};

export const useDeleteBrandIngredient = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ingredientId) => {
      const response = await makeApiRequest({
        endpoint: `/api/brand_ingredients/ingredient/${ingredientId}`,
        method: "DELETE"
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandIngredients", brandId] });
    }
  });
};

export const useUploadBrandIngredients = (brandId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (csvData) => {
      const response = await makeApiRequest({
        endpoint: `/api/brand_ingredients/csvupload?brandId=${brandId}`,
        method: "POST",
        body: { brandIngredientsCSV: csvData },
        json: true
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandIngredients", brandId] });
    }
  });
};
